// Mode agent (Réflexion activée) : le chat est délégué au backend agent
// (harness OpenHands + GLM-5.3-Flash via RCP, cf. repo
// outlookLLM-EPFL/glm-agent-server) au lieu de l'orchestration locale
// (recherche fixe + streamChatCompletion).
//
// SANS ÉTAT : l'historique de la conversation part avec chaque question (il
// vit dans IndexedDB comme en mode normal) ; le backend ne garde rien entre
// deux tours, ce qui autorise plusieurs réplicas et survit aux redémarrages.
//
// Principe :
//  - le backend fait tourner la boucle agentique (multi-passes, décisions de
//    recherche) et dispose côté serveur de search_epfl_knowledge (KB ServiceNow
//    + site EPFL, OBO avec notre id_token) ;
//  - les capacités qui vivent dans le navigateur (search_local sur les
//    documents personnels en IndexedDB, search_web Exa) sont déclarées comme
//    "tools client" : le backend émet un événement SSE `client_tool_request`,
//    on exécute l'implémentation locale, et on renvoie le résultat via
//    POST /tool-result. Les documents personnels ne quittent le navigateur
//    que sous forme d'extraits pertinents (comme avec le RAG local actuel).
//
// Auth : l'id_token Entra (getAuthToken) sert de garde d'accès au backend ET
// de token délégué pour la recherche KB — même modèle OBO que epflHybrid.
import { getAuthToken, renewAuthToken, SessionExpiredError } from "./auth";
import type { RetrievedChunkSnapshot } from "../db/types";
import { CITATION_RE, parseCitationMatch } from "./citations";

export interface UltraDeps {
  search_local?: (q: string) => Promise<RetrievedChunkSnapshot[]>;
  search_epfl?: (q: string) => Promise<RetrievedChunkSnapshot[]>;
  search_web?: (q: string) => Promise<RetrievedChunkSnapshot[]>;
  /** Lecture intégrale d'un document actif, par son nom (équivalent agent du
   *  mode FullText). */
  read_document?: (name: string) => Promise<RetrievedChunkSnapshot[]>;
}

/** Le backend agent n'a pas pu être joint (réseau, pod indisponible) AVANT
 *  toute réponse : l'appelant peut basculer sur le circuit standard. */
export class AgentUnavailableError extends Error {}

export interface UltraCallbacks {
  /** Delta de texte de la réponse (à accumuler dans pendingAssistant). */
  onDelta: (text: string) => void;
  /** Corpus de sources mis à jour (alimente pendingRetrieved / citations [N]). */
  onCorpus: (snaps: RetrievedChunkSnapshot[]) => void;
  /** Une recherche démarre côté agent (pour la ligne de statut). */
  onSearching?: () => void;
  /** Fragment de réflexion de l'agent (jamais mêlé à la réponse). */
  onThinking?: (text: string) => void;
  /** Outil exécuté côté serveur (les outils client passent par `deps`). */
  onTool?: (name: string) => void;
}

export interface UltraResult {
  full: string;
  retrieved: RetrievedChunkSnapshot[];
}

// Plafond par défaut du texte renvoyé par read_document, en caractères
// (~75 000 jetons). PolyChat passe le sien : le budget de lecture intégrale,
// le même qu'en mode Rapide, pour qu'un document lu en entier là le soit ici
// aussi (21.10.2026). Au-delà, le résultat le dit, et l'agent cherche le reste.
const READ_DOCUMENT_MAX_CHARS = 300_000;

function toolDefs(deps: UltraDeps, documentNames: string[]) {
  const defs: Array<{ name: string; description: string; input_schema: object }> = [];
  if (deps.search_local) {
    defs.push({
      name: "search_local",
      description:
        "Recherche sémantique dans les DOCUMENTS PERSONNELS que l'utilisateur a chargés " +
        "dans l'extension (contrats, rapports, procédures internes, documents DPO…). " +
        "Retourne des passages numérotés — cite-les au format [N: \"phrase exacte\"]. " +
        "À privilégier dès que la question porte sur les documents de l'utilisateur.",
      input_schema: {
        type: "object",
        properties: { query: { type: "string", description: "La recherche (question ou mots-clés)" } },
        required: ["query"],
      },
    });
  }
  if (deps.search_epfl) {
    defs.push({
      name: "search_epfl",
      description:
        "Recherche dans la BASE DE CONNAISSANCES EPFL : articles KB ServiceNow (procédures " +
        "IT, RH, administration — préfixés [KB]), incidents résolus ([INC]) et pages du site " +
        "EPFL. Résultats filtrés selon les droits de l'utilisateur. " +
        "Retourne des passages numérotés — cite-les au format [N: \"phrase exacte\"]. " +
        "Pour toute question « comment faire X à l'EPFL », procédure ou contact.",
      input_schema: {
        type: "object",
        properties: { query: { type: "string", description: "La question ou les mots-clés" } },
        required: ["query"],
      },
    });
  }
  if (deps.read_document && documentNames.length > 0) {
    defs.push({
      name: "read_document",
      description:
        "Lit un document personnel ACTIF EN ENTIER, dans l'ordre des pages. Pour résumer, " +
        "comparer ou vérifier l'exhaustivité d'un document — quand quelques passages ne " +
        "suffisent pas. Documents disponibles : " +
        documentNames.map((n) => `« ${n} »`).join(", ") +
        ". Retourne des passages numérotés — cite-les au format [N: \"phrase exacte\"].",
      input_schema: {
        type: "object",
        properties: { name: { type: "string", description: "Le nom exact du document" } },
        required: ["name"],
      },
    });
  }
  if (deps.search_web) {
    defs.push({
      name: "search_web",
      description:
        "Recherche sur le web ouvert (moteur Exa). Pour les informations générales, " +
        "actualités, ou tout ce qui n'est ni dans les documents personnels ni dans la base EPFL. " +
        "Retourne des passages numérotés — cite-les avec [N].",
      input_schema: {
        type: "object",
        properties: { query: { type: "string", description: "La recherche web" } },
        required: ["query"],
      },
    });
  }
  return defs;
}

/**
 * Envoie une question au backend Ultra et streame la réponse.
 * Exécute localement les tools demandés par l'agent (search_local/search_web).
 */
export async function runUltraChat(opts: {
  /** Question du tour, documents glissés dans le chat inclus. */
  question: string;
  /** Tours précédents, du plus ancien au plus récent. */
  history: Array<{ role: "user" | "assistant"; content: string }>;
  /** Images jointes à la question (data URLs). */
  images?: string[];
  /** Noms des documents actifs, proposés à read_document. */
  documentNames?: string[];
  backendUrl: string;
  /** Réflexion libre (true) ou réduite (`reasoning_effort: low`, false). Le
   *  modèle, lui, est fixé par le backend (GLM-5.3-Flash). */
  thinking: boolean;
  deps: UltraDeps;
  callbacks: UltraCallbacks;
  signal?: AbortSignal;
  /** Texte maximal rendu par read_document, en caractères. */
  readDocumentMaxChars?: number;
}): Promise<UltraResult> {
  const { question, deps, callbacks, signal } = opts;
  const readMax = opts.readDocumentMaxChars ?? READ_DOCUMENT_MAX_CHARS;
  const base = opts.backendUrl.replace(/\/$/, "");
  const send = async (idToken: string): Promise<Response> => {
    try {
      return await fetch(`${base}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Le cookie d'affinité de l'ingress (plusieurs réplicas) doit revenir
        // avec le POST /tool-result : même pod que celui qui attend le résultat.
        credentials: "include",
        body: JSON.stringify({
          profile: "dpo",
          thinking: opts.thinking,
          message: question,
          history: opts.history,
          images: opts.images ?? [],
          kbToken: idToken,
          clientTools: toolDefs(deps, opts.documentNames ?? []),
        }),
        signal,
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      throw new AgentUnavailableError(`Agent injoignable : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // L'agent vérifie lui-même le jeton EPFL. Refusé (expiré entre-temps,
  // horloges décalées) : un jeton neuf et un second essai, comme authFetch
  // pour notre serveur ; refusé encore, la session est morte et l'usager est
  // invité à se reconnecter, au lieu de l'erreur brute « Agent indisponible
  // (401) » (22.10.2026).
  let resp = await send(await getAuthToken());
  if (resp.status === 401) {
    const fresh = await renewAuthToken();
    if (!fresh) throw new SessionExpiredError();
    resp = await send(fresh);
    if (resp.status === 401) throw new SessionExpiredError();
  }
  if (!resp.ok || !resp.body) {
    const detail = await resp.text().catch(() => "");
    const msg = `Agent indisponible (${resp.status}) ${detail.slice(0, 200)}`;
    // 502/503/504 : pod absent ou en redémarrage — le circuit standard prend le
    // relais. Les autres codes (401…) sont de vraies erreurs à montrer.
    if (resp.status >= 502 && resp.status <= 504) throw new AgentUnavailableError(msg);
    throw new Error(msg);
  }

  // Corpus global de la réponse : les passages retournés par les tools locaux,
  // numérotés séquentiellement pour que les citations [N] du modèle matchent.
  const corpus: RetrievedChunkSnapshot[] = [];

  const executeClientTool = async (
    callId: string,
    name: string,
    input: { query?: string; name?: string },
  ) => {
    let result: string;
    let isError = false;
    try {
      const isRead = name === "read_document";
      const dep =
        name === "search_local" ? deps.search_local
        : name === "search_epfl" ? deps.search_epfl
        : name === "search_web" ? deps.search_web
        : isRead ? deps.read_document
        : undefined;
      if (!dep) throw new Error(`Tool client inconnu: ${name}`);
      callbacks.onSearching?.();
      let snaps = await dep(String((isRead ? input?.name : input?.query) ?? ""));
      let dropped = 0;
      if (isRead) {
        // Document entier, mais borné : on s'arrête au passage qui dépasse.
        let total = 0;
        const kept: RetrievedChunkSnapshot[] = [];
        for (const s of snaps) {
          if (total + s.text.length > readMax) break;
          kept.push(s);
          total += s.text.length;
        }
        dropped = snaps.length - kept.length;
        snaps = kept;
      }
      const base0 = corpus.length;
      const numbered = snaps.map((s, i) => ({ ...s, number: base0 + i + 1 }));
      corpus.push(...numbered);
      callbacks.onCorpus([...corpus]);
      result = JSON.stringify({
        count: numbered.length,
        results: numbered.map((s) => ({
          number: s.number,
          doc: s.docName,
          page: s.page,
          section: s.section,
          url: s.sourceUrl,
          // Recherche : extraits tronqués ; lecture : le texte entier.
          text: isRead ? s.text : s.text.slice(0, 1500),
        })),
        note: numbered.length > 0
          ? 'Cite chaque passage utilisé au format [number: "phrase exacte copiée du text"] — verbatim, pour le surlignage dans la source.'
          : "Aucun résultat.",
        ...(dropped > 0
          ? { truncated: `Document trop volumineux : ${dropped} passage(s) de la fin manquent. Cherche-les avec search_local.` }
          : {}),
      });
    } catch (e) {
      result = JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
      isError = true;
    }
    // Fire-and-forget : la boucle SSE ne doit pas attendre ce POST.
    fetch(`${base}/tool-result`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ call_id: callId, result, is_error: isError }),
    }).catch((e) => console.warn("[ultra] tool-result POST failed:", e));
  };

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";
  let finalResponse = "";
  let blockDelta = "";
  let streamedAnything = false;

  const handleEvent = (eventType: string, dataJson: string) => {
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(dataJson);
    } catch {
      return;
    }
    switch (eventType) {
      case "text_block_start":
        blockDelta = "";
        break;
      case "text_delta": {
        const t = data.text as string;
        if (t) {
          if (!blockDelta && streamedAnything) {
            full += "\n\n";
            callbacks.onDelta("\n\n");
          }
          blockDelta += t;
          full += t;
          streamedAnything = true;
          callbacks.onDelta(t);
        }
        break;
      }
      case "text": {
        const t = data.text as string;
        if (!t) break;
        if (blockDelta.trim()) {
          blockDelta = "";
          break; // déjà affiché via les deltas
        }
        const chunk = streamedAnything ? `\n\n${t}` : t;
        full += chunk;
        streamedAnything = true;
        callbacks.onDelta(chunk);
        break;
      }
      case "client_tool_request":
        void executeClientTool(data.call_id as string, data.name as string, (data.input as { query?: string; name?: string }) ?? {});
        break;
      case "thinking_delta": {
        const t = data.text as string;
        if (t) callbacks.onThinking?.(t);
        break;
      }
      case "tool_use": {
        callbacks.onSearching?.();
        // Les outils client (search_local…) s'inscrivent eux-mêmes au journal
        // avec leur requête, via `deps` ; seuls les outils serveur sont notés ici.
        const name = String(data.name ?? "").replace(/^mcp__[^_]+__/, "");
        if (name && !(name in deps)) callbacks.onTool?.(name);
        break;
      }
      case "result":
        if (typeof data.result === "string") finalResponse = data.result;
        break;
      case "error":
        throw new Error((data.message as string) || "Erreur du backend Ultra");
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let eventType = "";
      let dataJson = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event: ")) eventType = line.slice(7).trim();
        else if (line.startsWith("data: ")) dataJson += line.slice(6);
      }
      if (eventType && dataJson) handleEvent(eventType, dataJson);
    }
  }

  // Seuls les passages cités restent attachés à la réponse : en mode agent,
  // les recherches en remontent des dizaines (272 le 22.10.2026, dont une
  // vingtaine cités), et un passage que la réponse ne cite pas n'a servi à
  // rien. Les numéros ne changent pas ; pendant le tour, onCorpus montre tout.
  const answer = full || finalResponse;
  const cited = new Set<number>();
  for (const m of answer.matchAll(new RegExp(CITATION_RE.source, "g"))) {
    for (const n of parseCitationMatch(m).refs) cited.add(n);
  }
  return { full: answer, retrieved: corpus.filter((s) => cited.has(s.number)) };
}
